import { toast } from '@music-vine/cadence/ui';
import { captureException } from '@sentry/nextjs';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { useAccountDetails } from '@/hooks/billing/useAccountDetails';
import {
  CACHE_KEY as SETTINGS_KEY,
  type SettingsQueryResult,
  useSettings,
} from '@/hooks/state/useSettings';
import { usePatchApiV1SubscriptionSubscriptionId } from '@/open-api/subscription/subscription';
import { logEvent } from '@/pages/api/v1/utils';
import { useSubscriptionType } from '../auth';

/**
 * Hook to re-enable auto-renew for a subscription via the v1 PATCH
 * subscription endpoint. Optimistically flips auto-renew in the settings
 * cache for instant UI feedback and rolls back on failure.
 *
 * @returns Object containing the enableAutoRenew function and loading state
 *
 * @example
 * ```tsx
 * const { enableAutoRenew, isUpdating } = useEnableAutoRenew();
 *
 * <Button onClick={enableAutoRenew} disabled={isUpdating}>
 *   {isUpdating ? 'Reactivating...' : 'Reactivate subscription'}
 * </Button>
 * ```
 */
export const useEnableAutoRenew = () => {
  const { data: accountDetails } = useAccountDetails();
  const queryClient = useQueryClient();
  const router = useRouter();
  const subscriptionType = useSubscriptionType();
  const { settings } = useSettings();
  const subscriptionId = settings?.subscriptionData?.subscriptionId;

  const { mutateAsync, isPending } = usePatchApiV1SubscriptionSubscriptionId({
    mutation: {
      onMutate: () => {
        const previousData =
          queryClient.getQueryData<SettingsQueryResult>(SETTINGS_KEY);
        const subscriptionData = previousData?.siteSettings.subscriptionData;

        if (previousData && subscriptionData) {
          queryClient.setQueryData<SettingsQueryResult>(SETTINGS_KEY, {
            ...previousData,
            siteSettings: {
              ...previousData.siteSettings,
              subscriptionData: { ...subscriptionData, autoRenew: true },
            },
          });
        }

        return { previousData };
      },
      onSuccess: () => {
        void logEvent({
          event_type: 'enable_auto_renew',
          asPath: router.asPath,
          metadata: {
            plan_type: subscriptionType,
          },
        });
      },
      onError: (error, _variables, context) => {
        queryClient.setQueryData(SETTINGS_KEY, context?.previousData);

        captureException(error, {
          tags: {
            component: 'useEnableAutoRenew',
            operation: 'patchApiV1SubscriptionSubscriptionId',
          },
          extra: { subscriptionId },
        });
      },
    },
  });

  const enableAutoRenew = useCallback(() => {
    if (!(subscriptionId && accountDetails?.form?.subscription?.autoRenew)) {
      return;
    }

    const request = mutateAsync({
      subscriptionId,
      data: { subscriptionId, autoRenew: true },
    });

    toast.promise(request, {
      loading: { title: 'Reactivating subscription...' },
      success: { title: 'Subscription reactivated' },
      error: { title: 'Unable to reactivate subscription' },
    });

    // rejection is surfaced by the toast; rollback happens in onError
    request.catch(() => undefined);
  }, [
    subscriptionId,
    accountDetails?.form?.subscription?.autoRenew,
    mutateAsync,
  ]);

  return { enableAutoRenew, isUpdating: isPending };
};
